import { CreditCardIcon } from '@heroicons/react/24/outline'
import { AlertTriangle, ArrowUpRight, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router'

import { ISubscription } from '@/MODELS/subscription.model'

import { camelCaseToTitleCase } from '@/utils/form-utils'

import Button from '@/components/Button'
import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'

import { usePaywall } from './PayWallProvider'

export default function ManageSubscription() {
    const navigate = useNavigate()
    const { subscription, manageSubscription, isLoading, paywallMode } = usePaywall()

    const formatDate = (date: Date | string) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
    }

    return (
        <div className='flex-1 mx-auto'>
            <SectionHeader title='Manage Subscription' />

            {isLoading ? (
                <Loading mode='block' text='Loading subscription details...' />
            ) : subscription ? (
                <div className='p-4 space-y-6'>
                    <div className='bg-white border-gray-200 rounded-lg shadow-sm'>
                        <div className='p-6'>
                            <div className='flex items-start justify-between'>
                                <div>
                                    <div className='flex items-center gap-2'>
                                        <span className='text-lg font-medium text-gray-900'>
                                            BrightPath Starter Plan
                                        </span>
                                        <StripeSubscriptionBadge subscription={subscription} />
                                    </div>
                                    <p className='mt-1 text-sm text-gray-600'>Access to all features and resources</p>
                                </div>
                            </div>

                            {subscription.benefitsEndDate && (
                                <div className='p-4 mt-4 border rounded-md bg-secondary-50 border-secondary-200'>
                                    <div className='flex items-center gap-2'>
                                        <AlertTriangle className='w-5 h-5 text-secondary-600' />
                                        <p className='text-sm text-secondary-800'>
                                            <span className='font-medium'>
                                                Subscription ends on {formatDate(subscription.benefitsEndDate)}
                                            </span>
                                            <br />
                                            You'll continue to have access to premium features until this date.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='bg-white border-gray-100 rounded-lg shadow-sm'>
                        <div className='px-6 py-3'>
                            <h2 className='text-lg font-semibold text-gray-900'>Subscription Details</h2>
                        </div>
                        <div className='px-6 pb-5'>
                            <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                                <div>
                                    <label className='text-sm font-medium text-gray-500'>Status</label>
                                    <p className='mt-1 text-sm text-gray-900 capitalize'>
                                        {subscription.subscriptionStatus === 'trialing'
                                            ? '30-Day Free Trial'
                                            : subscription.subscriptionStatus?.replace('_', ' ')}
                                    </p>
                                </div>
                                <div className='flex justify-between'>
                                    <div>
                                        <label className='text-sm font-medium text-gray-500'>Started</label>
                                        <p className='mt-1 text-sm text-gray-900'>
                                            {formatDate(subscription.createdAt)}
                                        </p>
                                    </div>
                                    {subscription.benefitsEndDate && (
                                        <div>
                                            <label className='text-sm font-medium text-gray-500'>Access Until</label>
                                            <p className='mt-1 text-sm text-gray-900'>
                                                {formatDate(subscription.benefitsEndDate)}
                                            </p>
                                        </div>
                                    ) }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='py-3 bg-white rounded-lg shadow-sm'>
                        <div className='px-6 py-3 border-gray-200'>
                            <h2 className='text-lg font-semibold text-gray-900'>Manage Subscription</h2>
                        </div>
                        <div className='px-6 pb-3 space-y-4'>
                            <p className='text-sm text-gray-600'>
                                Use the customer portal to update payment methods, download invoices, change your plan,
                                or cancel your subscription.
                            </p>

                            <Button
                                onClick={manageSubscription}
                                className='inline-flex items-center px-4 py-2 text-sm font-medium text-white transition-colors rounded-md bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
                            >
                                <CreditCardIcon className='w-4 h-4 mr-2' />
                                Manage Billing & Subscription
                                <ExternalLink className='w-4 h-4 ml-2' />
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='p-12 m-6 text-center bg-white rounded-lg shadow-sm'>
                    <CreditCardIcon className='w-12 h-12 mx-auto mb-4 text-gray-300' />
                    <h3 className='mb-2 text-lg font-medium text-gray-900'>No Active Subscription</h3>
                    <p className='mb-6 text-gray-500'>
                        Subscribe to BrightPath Starter Plan to access all features and resources
                    </p>
                    <div className='space-y-3'>
                        <Button
                            onClick={() => navigate(`/stripe-callback?mode=${paywallMode}`)}
                            className='inline-flex items-center px-6 py-3 text-sm font-medium text-white transition-colors rounded-md bg-brand-600 hover:bg-brand-700'
                        >
                            View Pricing Plans
                            <ArrowUpRight className='w-4 h-4 ml-2' />
                        </Button>
                        {/* <div className='text-xs text-gray-500'>7-day free trial • Cancel anytime</div> */}
                    </div>
                </div>
            )}
        </div>
    )
}

export const StripeSubscriptionBadge = (props: { subscription: ISubscription }) => {
    const colors = {
        active: 'bg-green-50 text-green-700 ring-green-600/20',
        trialing: 'bg-green-50 text-green-700 ring-green-600/20',
        paused: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
        canceled: 'bg-red-50 text-red-700 ring-red-600/10',
        incomplete: 'bg-red-50 text-red-700 ring-red-600/10',
        incomplete_expired: 'bg-red-50 text-red-700 ring-red-600/10',
        unpaid: 'bg-red-50 text-red-700 ring-red-600/10',
        past_due: 'bg-red-50 text-red-700 ring-red-600/10',
    }

    return (
        <div
            className={`inline-flex items-center px-2 py-1 text-sm font-medium rounded-full ring-1 ring-inset ${colors[props?.subscription?.subscriptionStatus ?? 'canceled']}`}
        >
            {camelCaseToTitleCase(props?.subscription?.subscriptionStatus ?? 'Not Subscribed')}
        </div>
    )
}
